import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_MENU_PANEL, MatMenu, MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, take } from 'rxjs';
import { NavBadgeFormatPipe } from 'src/app/pipes/nav-badge-format.pipe';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import {
  ColorScheme,
  PencilBottomSheetContainerComponent,
} from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from 'src/app/standalones/components/pencil-button/pencil-button.component';
import {
  IconTypes,
  PencilIconComponent,
} from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { PencilSpinnerComponent } from 'src/app/ui/pencil-spinner/pencil-spinner.component';

@Component({
  selector: 'app-nav-sidebar-item',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    NavBadgeFormatPipe,
    PencilBottomSheetContainerComponent,
    PencilButtonComponent,
    PencilIconComponent,
    PencilSpinnerComponent,
  ],
  templateUrl: './nav-sidebar-item.component.html',
  styleUrls: ['./nav-sidebar-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavSidebarItemComponent implements OnInit, OnDestroy {
  @Input() label = '';
  @Input() icon?: string;
  @Input() iconType: IconTypes = IconTypes.lucideIcon;
  @Input() active = false;
  @Input() expanded = false;
  @Input() badge?: number | null;
  @Input() indentationLevel = 0;
  @Input() showChevron = false;
  @Input() danger = false;
  @Input() disabled = false;
  /** Swaps the leading icon for a spinner, for rows that stand in for content still loading. */
  @Input() loading = false;
  /** Emphasises the label, for rows that state something rather than navigate somewhere. */
  @Input() bold = false;
  @Input() dataIntercomTarget?: string;

  /**
   * The row's actions, supplied by the parent. A TemplateRef rather than projected content
   * because it's rendered in two places — the menu panel and, on small screens, a bottom sheet.
   * Providing it is what puts the "more" button on the row.
   */
  @Input() menuContent?: TemplateRef<unknown>;
  /** Passed through to `menuContent` as `$implicit`, so a shared template can act on this row. */
  @Input() menuContext?: unknown;

  @Output() itemClick = new EventEmitter<Event>();

  @ViewChild(MatMenu, { static: true }) private itemMenu?: MatMenu;

  /**
   * Hands `menuContent` this panel, so a menu item projected in by the parent can open a submenu
   * beside the menu rather than on top of it — see the note in the template.
   */
  protected menuPanelInjector: Injector | null = null;
  @ViewChild('bottomSheetContainer') private bottomSheetContainer?: TemplateRef<unknown>;

  /** Keeps the button on screen while its menu is open, even once the pointer leaves the row. */
  readonly isMenuOpen$ = new BehaviorSubject(false);

  protected readonly IconTypes = IconTypes;
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly ColorScheme = ColorScheme;

  private bottomSheetRef?: MatBottomSheetRef;

  constructor(
    private bottomSheet: MatBottomSheet,
    private injector: Injector,
    protected screenSizeService: ScreenSizeService,
  ) {}

  ngOnInit(): void {
    this.menuPanelInjector = Injector.create({
      providers: [{ provide: MAT_MENU_PANEL, useValue: this.itemMenu }],
      parent: this.injector,
    });
  }

  @HostBinding('class.active') get activeClass(): boolean {
    return this.active;
  }

  @HostBinding('class.danger') get dangerClass(): boolean {
    return this.danger;
  }

  @HostBinding('attr.data-intercom-target') get intercomTarget(): string | undefined {
    return this.dataIntercomTarget;
  }

  get paddingLeft(): number {
    return this.indentationLevel ? 36 : 10;
  }

  ngOnDestroy(): void {
    this.dismissBottomSheet();
  }

  handleClick(event: Event): void {
    if (this.disabled) {
      return;
    }
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    this.itemClick.emit(event);
  }

  /**
   * Bound on the button host, not its `clickEvent` output — stopping there would pre-empt
   * MatMenuTrigger's own listener on this element and the menu would never open.
   */
  onMenuButtonClick(event: Event): void {
    event.stopPropagation();

    if (event instanceof KeyboardEvent) {
      return;
    }

    if (this.screenSizeService.renderMenuAsBottomSheet) {
      this.openBottomSheet();
    }
  }

  dismissBottomSheet(): void {
    this.bottomSheetRef?.dismiss();
    this.bottomSheetRef = undefined;
    this.isMenuOpen$.next(false);
  }

  onMenuOpened(): void {
    this.isMenuOpen$.next(true);
  }

  onMenuClosed(): void {
    this.isMenuOpen$.next(false);
  }

  private openBottomSheet(): void {
    if (!this.bottomSheetContainer) {
      return;
    }

    this.isMenuOpen$.next(true);
    this.bottomSheetRef = this.bottomSheet.open(this.bottomSheetContainer, {
      panelClass: ['pencil-bottom-sheet', 'light-scheme'],
      closeOnNavigation: true,
    });

    this.bottomSheetRef
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        this.bottomSheetRef = undefined;
        this.isMenuOpen$.next(false);
      });
  }
}
